import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import Logo from '../../components/Logo'
import { Eye, EyeOff, AlertCircle, ArrowRight } from 'lucide-react'

export default function Login() {
  const navigate = useNavigate()
  const { signIn, user, profile, loading: authLoading } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user && profile) {
      if (profile.role === 'admin') {
        navigate('/admin', { replace: true })
      } else {
        navigate('/garage', { replace: true })
      }
    }
  }, [user, profile, authLoading, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const { error } = await signIn(email, password)

      if (error) {
        if (error.message.includes('Invalid login')) {
          setError('Invalid email or password')
        } else if (error.message.includes('Email not confirmed')) {
          setError('Please check your email and confirm your account first')
        } else {
          setError(error.message)
        }
        return
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-rdc-cream flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-rdc-cream flex">
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-white items-center justify-center p-12 border-r border-rdc-stone/30">
        <div className="max-w-sm">
          <Logo size="large" className="mb-10" />

          <h2 className="page-title mb-4">
            Your Digital Garage
          </h2>

          <p className="text-body leading-relaxed mb-8">
            A refined platform for the discerning collector. Manage your vehicles, track their history, and connect with specialists who understand the art of automotive care.
          </p>

          <div className="space-y-4">
            {[
              'Complete vehicle history & documentation',
              'Smart maintenance reminders',
              'Collection value tracking',
              'Direct concierge access'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-rdc-orange" />
                <span className="text-sm text-rdc-charcoal">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-10 flex justify-center">
            <Logo />
          </div>

          <div className="card p-8 lg:p-10">
            <div className="mb-8">
              <p className="text-label mb-2">Welcome back</p>
              <h1 className="page-title">Sign In</h1>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-rdc-burgundy/5 border border-rdc-burgundy/20 rounded-xl flex items-start gap-3">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5 text-rdc-burgundy" strokeWidth={2} />
                <span className="text-sm text-rdc-burgundy">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="form-group mb-0">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  disabled={loading}
                />
              </div>

              <div className="form-group mb-0">
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="form-label mb-0">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-rdc-orange hover:text-rdc-orange/80 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pr-12"
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-rdc-taupe hover:text-rdc-charcoal transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} strokeWidth={1.75} /> : <Eye size={18} strokeWidth={1.75} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full mt-6"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="loading-spinner w-4 h-4 border-2" />
                    Signing in...
                  </span>
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={16} strokeWidth={2} />
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-rdc-stone/30 text-center">
              <p className="text-sm text-rdc-taupe">
                Don't have an account?{' '}
                <Link to="/signup" className="font-medium text-rdc-orange hover:text-rdc-orange/80 transition-colors">
                  Create one free
                </Link>
              </p>
            </div>
          </div>

          {/* Footer */}
          <p className="mt-8 text-center text-xs text-rdc-taupe">
            By signing in, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  )
}
